/* Royal Mist: delivery line under the buy buttons (blocks/rm-delivery-line.liquid).
   In stock: counts down to today's cut-off in Nairobi time, or names the next delivery run when closed.
   Sold out: the block renders a WhatsApp "Tell me when it's back" link instead (Liquid only).
   Follows the variant picker the same way the theme's own stock block does. */
import { morph } from '@theme/morph';
import { StandardEvents } from '@shopify/events';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const ampm = (h) => (h % 12 || 12) + (h >= 12 ? 'pm' : 'am');
const nairobiNow = () => {
  const d = new Date();
  return new Date(d.getTime() + d.getTimezoneOffset() * 60000 + 3 * 3600000);
};

class RmDeliveryLine extends HTMLElement {
  connectedCallback() {
    this.scope = this.closest('.shopify-section, dialog');
    this.scope?.addEventListener(StandardEvents.productSelect, this.#onSelect);
    document.addEventListener('visibilitychange', this.#onShow);
    this.#tick();
    this.timer = setInterval(this.#tick, 15000);
  }

  disconnectedCallback() {
    this.scope?.removeEventListener(StandardEvents.productSelect, this.#onSelect);
    document.removeEventListener('visibilitychange', this.#onShow);
    clearInterval(this.timer);
  }

  #onShow = () => {
    if (!document.hidden) this.#tick();
  };

  #onSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      ?.then(({ detail }) => {
        if (!detail?.html) return;

        if (detail.newProduct) {
          this.dataset.productId = detail.newProduct.id;
        } else if (detail.productId && String(detail.productId) !== this.dataset.productId) {
          return;
        }

        const fresh = detail.html.querySelector('rm-delivery-line');
        if (!fresh) return;

        morph(this, fresh, { childrenOnly: true });
        this.#tick();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[rm-delivery-line] Event promise rejected:', error);
      });
  };

  #hour(name, fallback) {
    const value = parseInt(this.dataset[name], 10);
    return Number.isNaN(value) ? fallback : value;
  }

  #tick = () => {
    const lead = this.querySelector('[data-rm-dl-lead]');
    const text = this.querySelector('[data-rm-dl-text]');
    if (!lead || !text) return;

    const open = this.#hour('open', 9);
    const cutoff = this.#hour('cutoff', 18);
    const satClose = this.#hour('satClose', 16);
    const sunClose = this.#hour('sunClose', 0);
    const closeOn = (day) => (day === 0 ? sunClose : day === 6 ? satClose : cutoff);
    const isOpenDay = (day) => closeOn(day) > open;

    const now = nairobiNow();
    const day = now.getDay();
    const h = now.getHours();

    if (isOpenDay(day) && h >= open && h < closeOn(day)) {
      const end = new Date(now.getTime());
      end.setHours(closeOn(day), 0, 0, 0);
      const mins = Math.max(1, Math.ceil((end - now) / 60000));
      const hh = Math.floor(mins / 60);
      const mm = mins % 60;
      const left = hh ? hh + 'h' + (mm ? ' ' + mm + 'm' : '') : mm + 'm';
      this.dataset.state = 'open';
      lead.textContent = 'In stock, on today’s run';
      text.innerHTML = 'Order within <b>' + left + '</b> for same-day delivery in Nairobi';
    } else {
      let add = isOpenDay(day) && h < open ? 0 : 1;
      while (add < 7 && !isOpenDay((day + add) % 7)) add++;
      const when = add === 0 ? 'today’s' : add === 1 ? 'tomorrow’s' : DAYS[(day + add) % 7] + '’s';
      this.dataset.state = 'closed';
      lead.textContent = 'In stock';
      text.innerHTML = 'Order now for <b>' + when + ' ' + ampm(open) + ' run</b>';
    }
  };
}

if (!customElements.get('rm-delivery-line')) {
  customElements.define('rm-delivery-line', RmDeliveryLine);
}
